import { RedirectCommand, Router, Routes } from '@angular/router';
import { roleGuard } from './core/guards/role.guard';
import { featureFlagGuard } from './core/guards/feature-flag.guard';
import { userInfoGuard } from './features/user/guards/user-info.guard';
import { provideTranslocoScope } from '@jsverse/transloco';
import { ORGANIZATION_ROUTES } from './features/organizations/organizations.routes';
import { inject } from '@angular/core';
import { UserInfoService } from './features/user/services/user-info.service';
import { demoGuard } from './features/demo/guards/demo.guard';
import { organizationsLoadedGuard } from './features/organizations/guards/organizations-loaded.guard';

export const routes: Routes = [
  {
    path: 'onboarding/user-info',
    loadComponent: () =>
      import('./features/user/pages/user-info-onboarding-page/user-info-onboarding-page').then(
        (m) => m.UserInfoOnboardingPage,
      ),
  },
  {
    path: 'organizations',
    canActivate: [userInfoGuard],
    children: ORGANIZATION_ROUTES,
  },
  {
    path: 'broker',
    canActivate: [userInfoGuard],
    children: [
      {
        path: 'organizations',
        canActivate: [
          roleGuard('USER_ADMIN'),
          // load all organizations via store for the user filter
          organizationsLoadedGuard,
        ],
        loadComponent: () =>
          import('./features/organizations/pages/organizations-table-page/organizations-table-page').then(
            (m) => m.OrganizationsTablePage,
          ),
      },
      {
        path: 'documents',
        canActivate: [roleGuard('USER_ADMIN')],
        loadChildren: () =>
          import('./features/broker-documents/broker-documents.routes').then(
            (m) => m.BROKER_DOCUMENTS_ROUTES,
          ),
      },
      {
        path: 'parse-requests',
        canActivate: [roleGuard('USER_ADMIN'), organizationsLoadedGuard],
        loadChildren: () =>
          import('./features/parse-requests/parse-requests.routes').then(
            (m) => m.BROKER_PARSE_REQUESTS_ROUTES,
          ),
      },
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'organizations',
      },
    ],
  },
  {
    path: 'user',
    canActivate: [userInfoGuard],
    loadChildren: () => import('./features/user/user.routes').then((m) => m.USER_ROUTES),
  },
  {
    path: 'public/:token',
    loadComponent: () =>
      import('./features/documents/pages/document-share-public-page/document-share-public-page').then(
        (m) => m.DocumentSharePublicPage,
      ),
  },
  {
    path: 'help-center',
    canActivate: [featureFlagGuard('helpCenter')],
    providers: [provideTranslocoScope('helpCenter')],
    loadChildren: () =>
      import('./features/help-center/help-center.routes').then((m) => m.HELP_CENTER_ROUTES),
  },
  {
    path: 'release-notes',
    canActivate: [featureFlagGuard('releaseNotes')],
    providers: [provideTranslocoScope('releaseNotes')],
    loadChildren: () =>
      import('./features/release-notes/release-notes.routes').then((m) => m.RELEASE_NOTES_ROUTES),
  },
  {
    path: 'excellence-ui-demo',
    loadChildren: () => import('excellence-ui').then((m) => m.EXCELLENCE_UI_DEMO_ROUTES),
  },
  {
    path: 'demo',
    canActivate: [demoGuard],
    providers: [provideTranslocoScope('demo')],
    loadComponent: () =>
      import('./features/demo/pages/demo-page/demo-page').then((m) => m.DemoPage),
  },
  {
    path: 'admin',
    canActivate: [userInfoGuard, roleGuard('USER_ADMIN')],
    loadChildren: () => import('./features/home/home-routes').then((m) => m.HOME_ROUTES),
  },
  {
    path: '',
    pathMatch: 'full',
    canActivate: [
      () => {
        const router = inject(Router);
        const role = inject(UserInfoService).info()?.role;
        switch (role) {
          case 'USER_ADMIN':
            return new RedirectCommand(router.parseUrl('/broker'));
          default:
            return new RedirectCommand(router.parseUrl('/organizations'));
        }
      },
    ],
    children: [],
  },
];
